import React, { useCallback, useRef, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Box, Stack } from '@mui/material';
import { LicenseManager } from 'ag-grid-enterprise';
import { CellClickedEvent, CellRendererSelectorFunc, GetRowIdParams } from 'ag-grid-community';
import { getAssignments, getApprovedOrRejectedAssignments, getAssignmentFilterValues } from '../../services/assignment';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import GridTopActions from './GridTopActions';
import AGGridHoc from '../../hoc/AGGridHoc';
import { cellTypes } from './CellTypes';
import { GetAssignmentRes } from '../../types/assignments';
import PreviewDrawer from './PreviewDrawer';
import VehicleListLoader from '../VehiclesGrid/VehicleListLoader';
import useFilters from '../../hooks/useFilters';
import { useAppSelector } from '../../hooks/useRedux';
import AppliedFilters from '../VehiclesGrid/AppliedFilters';
import groupingFn from '../../utils/grouping';

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY as string);

const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
};

const gridOptions = {
  suppressMenuHide: false,
  tooltipShowDelay: 400,
  defaultColDef,
  enableCharts: true,
  animateRows: true,
  enableRangeSelection: true,
  rowDragManaged: true,
  headerHeight: 30,
  rowHeight: 35,
  pagination: true,
};

const RejectedAssignmentsGridNew = () => {
  const [valuesArrayList, setValuesArrayList] = useState([]);
  let tempData: any = {};
  const { getFilterData } = useFilters();

  const appliedFilter = useAppSelector((state) => state.filter.appliedFilter);
  const [filterModel, setFilterModel] = useState<{ [key: string]: any }>();
  const areFiltersApplied = filterModel && !!Object.keys(filterModel).length;

  const gridHeight = areFiltersApplied ? '200px' : '150px';
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<any>(null);
  const [inputText, setInputText] = useState('');
  const [previewData, setPreviewData] = useState<any>(null);
  const perPage = 50;
  const [groupBy, setGroupBy] = useState<any>();

  const getFilterValuesAsync = async (params: any) => {
    const col = params.colDef.field;
    const data = await getAssignmentFilterValues('Rejected', col);
    return params.success(data.data);
  };

  const filterParamsTemp = {
    values: getFilterValuesAsync,
  };

  const [columnDefs, setColumnDefs] = useState([
    {
      headerName: 'Request Number',
      headerTooltip: 'Request Number',
      field: 'requestNumber',
      sortable: true,
      type: 'Link',
      cellRenderer: 'Link',
      width: 170,
      filter: true,
      filterParams: filterParamsTemp,
    },
    {
      headerName: 'Request date',
      headerTooltip: 'Request Date',
      field: 'requestDate',
      sortable: true,
      type: 'Plain',
      cellRenderer: 'Plain',
      width: 170,
      enableRowGroup: true,
      filterParams: filterParamsTemp,
    },
    {
      headerName: 'Requested By',
      headerTooltip: 'Requested By',
      field: 'requestedBy',
      sortable: true,
      type: 'Plain',
      cellRenderer: 'Plain',
      width: 170,
      enableRowGroup: true,
      filterParams: filterParamsTemp,
    },
    {
      headerName: 'Rejected date',
      headerTooltip: 'Rejected Date',
      field: 'modifiedDate',
      sortable: true,
      type: 'Plain',
      cellRenderer: 'Plain',
      width: 170,
      enableRowGroup: true,
      filterParams: filterParamsTemp,
    },
    {
      headerName: 'Rejected By',
      headerTooltip: 'Rejected By',
      field: 'modifiedBy',
      sortable: true,
      type: 'Plain',
      cellRenderer: 'Plain',
      width: 170,
      enableRowGroup: true,
      filterParams: filterParamsTemp,
    },
    {
      headerName: 'Rejection reason',
      headerTooltip: 'Rejection reason',
      field: 'reason',
      sortable: true,
      type: 'Plain',
      cellRenderer: 'Plain',
      width: 230,
      enableRowGroup: true,
      filterParams: filterParamsTemp,
    },
    {
      headerName: 'Rejection Type',
      headerTooltip: 'Rejection Type',
      field: 'rejectionType',
      sortable: true,
      type: 'Plain',
      cellRenderer: 'Plain',
      width: 230,
      enableRowGroup: true,
      filterParams: filterParamsTemp,
    },
    {
      headerName: 'Rejected Vehicle',
      marryChildren: true,
      children: [
        {
          headerName: 'Batch #',
          headerTooltip: 'Batch Number',
          field: 'inventoryVehicle.batchNumber',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 180,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'VIN',
          headerTooltip: 'VIN',
          field: 'inventoryVehicle.vin',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 180,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Year',
          headerTooltip: 'Year',
          field: 'inventoryVehicle.year',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 100,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Model',
          headerTooltip: 'Model',
          field: 'inventoryVehicle.model',
          sortable: true,
          filter: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 160,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Variant',
          headerTooltip: 'Variant',
          field: 'inventoryVehicle.variant',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 180,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Trim',
          headerTooltip: 'Trim',
          field: 'inventoryVehicle.trim',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 180,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Current City',
          headerTooltip: 'Current City',
          field: 'inventoryVehicle.currentCity',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 120,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Current State',
          headerTooltip: 'Current State',
          field: 'inventoryVehicle.currentState',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 120,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
      ],
    },
    {
      headerName: 'Original Vehicle',
      headerTooltip: 'Original Vehicle',
      marryChildren: true,
      children: [
        {
          headerName: 'Batch #',
          headerTooltip: 'Batch Number',
          field: 'customerVehicle.batchNumber',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 180,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'VIN',
          headerTooltip: 'VIN',
          field: 'customerVehicle.vin',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 180,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Year',
          headerTooltip: 'Year',
          field: 'customerVehicle.year',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 100,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Model',
          headerTooltip: 'Model',
          field: 'customerVehicle.model',
          sortable: true,
          filter: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 160,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Variant',
          headerTooltip: 'Variant',
          field: 'customerVehicle.variant',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 180,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Trim',
          headerTooltip: 'Trim',
          field: 'customerVehicle.trim',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 180,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Current City',
          headerTooltip: 'Current City',
          field: 'customerVehicle.currentCity',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 120,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
        {
          headerName: 'Current State',
          headerTooltip: 'Current State',
          field: 'customerVehicle.currentState',
          sortable: true,
          type: 'Plain',
          cellRenderer: 'Plain',
          width: 120,
          enableRowGroup: true,
          filterParams: filterParamsTemp,
        },
      ],
    },
  ]);

  let tempGroupedData: any = [];

  const datasource = {
    getRows(params: any) {
      console.log(JSON.stringify(params.request, null, 1));
      const { startRow, endRow, rowGroupCols, sortModel, groupKeys, filterModel: paramFilter } = params.request;
      /* Pagination */
      const page = endRow && endRow / 50;

      /* Sorting */
      const sort = `${sortModel[0]?.sort}:${sortModel[0]?.colId}`;
      const sortStatus = sortModel?.length > 0;

      /* Sidebar Filter */
      let tempFilter = { temp: { values: [], filterType: 'set' } };
      const tempFilterKeys: any = [];
      if (paramFilter) {
        tempFilter = paramFilter;
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of Object.entries(tempFilter)) {
          if (value && value?.values?.length > 0) {
            let valArr: any = value?.values;
            if (key.includes('Date')) {
              valArr = valArr.map((x: any) => {
                const d = new Date(x);
                /* eslint-disable prefer-template */
                return [d.getFullYear(), ('0' + (d.getMonth() + 1)).slice(-2), ('0' + d.getDate()).slice(-2)].join('-');
              });
            }
            valArr = valArr.map((x: any) => {
              if (x?.includes('.com')) {
                /* eslint-disable no-unsafe-optional-chaining */
                const [one, two] = x?.split('@');
                x = encodeURIComponent(one) + '@' + two;
              }
              return x;
            });
            const temp = `${key}:${valArr}`;
            tempFilterKeys.push(temp);
          }
        }
      }

      /* Grouping */
      const grouping = rowGroupCols?.slice(-1)[0]?.field;
      let groupByData;
      if (groupKeys.length > 0) {
        const myArr = groupKeys[0]?.split('(')[0];
        groupByData = `${grouping}:${myArr}`;
      }
      let arr: any = [];
      if (grouping && arr.length > 0 && groupKeys.length === 0) {
        const numberOfItemsPerPage = 50;
        const start = (page - 1) * numberOfItemsPerPage + 1;
        const end = start + numberOfItemsPerPage;
        const tempNewAllRowData: any = tempGroupedData.slice(start, end);
        let arrTemp: any = [];

        if (grouping && groupKeys.length === 0) {
          arrTemp = groupingFn(grouping, tempNewAllRowData);
        }
        params.successCallback(arrTemp, tempGroupedData.length);
      } else {
        getApprovedOrRejectedAssignments(
          'Rejected',
          50,
          page,
          sortStatus,
          sort,
          inputText,
          grouping,
          groupKeys,
          tempFilterKeys
        )
          .then((newAllRowData: any) => {
            const lastRow = newAllRowData?.totalData ? newAllRowData?.totalData : newAllRowData?.data?.length;
            arr = newAllRowData?.data;
            if (grouping && page === 1) {
              tempGroupedData = newAllRowData?.data;
            }
            if (grouping && groupKeys.length === 0) {
              arr = groupingFn(grouping, newAllRowData?.data);
            }
            setValuesArrayList(newAllRowData?.data.map((a: any) => a.requestNumber));
            tempData = getFilterData(arr);
            params.successCallback(arr, lastRow);
          })
          .catch(() => {
            params.successCallback([], 0);
          });
      }
    },
  };
  const onGridReady: any = (params: any) => {
    gridRef.current?.api.closeToolPanel();
    setGridApi(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridApi) {
      onGridReady(gridApi);
    }
  }, [inputText]);

  useEffect(() => {
    if (groupBy) {
      gridApi?.columnApi?.setRowGroupColumns([groupBy]);
    }
  }, [groupBy]);

  const onColumnRowGroupChanged = (event: any) => {
    if (event.columns && event.columns.slice(-1)[0]?.rowGroupActive) {
      setGroupBy(event.columns.slice(-1)[0]?.colId);
    } else if (event.columns.slice(-1)[0]?.rowGroupActive === false) {
      setGroupBy('');
    }
  };

  const closeFilter = () => {
    gridRef.current?.api.closeToolPanel();
  };

  const onToggleToolPanelClick = useCallback(async (panelId: 'columns' | 'filters') => {
    if (gridRef.current?.api.getOpenedToolPanel() === panelId) {
      gridRef.current?.api.closeToolPanel();
    } else {
      gridRef.current?.api.openToolPanel(panelId);
    }
  }, []);
  const onFilterChanged = (event: any) => {
    setFilterModel(event.api.getFilterModel());
  };

  const onCellClicked = useCallback((event: CellClickedEvent) => {
    if (event.colDef.field === 'requestNumber') {
      setPreviewData(event.data);
    }
  }, []);
  return (
    <>
      <Box className="top-section">
        <GridTopActions
          onToggleToolPanelClick={onToggleToolPanelClick}
          setInputText={setInputText}
          inputText={inputText}
        />
      </Box>
      {areFiltersApplied && <AppliedFilters filterModel={filterModel} gridRef={gridRef} columnDefs={columnDefs} />}
      <Box width="100%" height={`calc(95vh - ${gridHeight})`} className="ag-theme-material">
        <AGGridHoc gridRef={gridRef}>
          <AgGridReact
            {...gridOptions}
            columnDefs={columnDefs}
            rowModelType="serverSide"
            rowSelection="single"
            ref={gridRef}
            sideBar
            pagination
            onFilterChanged={onFilterChanged}
            components={cellTypes}
            onCellFocused={closeFilter}
            cacheBlockSize={perPage}
            paginationPageSize={perPage}
            onGridReady={onGridReady}
            paginateChildRows
            maxBlocksInCache={1}
            onCellClicked={onCellClicked}
            onColumnRowGroupChanged={onColumnRowGroupChanged}
          />
        </AGGridHoc>
      </Box>
      {previewData !== null && (
        <PreviewDrawer
          open={previewData !== null}
          onClose={() => setPreviewData(null)}
          data={previewData as GetAssignmentRes}
        />
      )}
    </>
  );
};

export default RejectedAssignmentsGridNew;
